/**
 * SalvationAdminService
 * Console surface of Salvation Management ("Salvation Team", /app/salvation): overview, people, follow-ups, first
 * assignment and corrections, reopen / close / review, auto-allocation, CSV export, campus teams and members, settings.
 * Campus leaders see and change only the teams they lead (403 not_your_campus).
 * Route: salvation/api/salvation/admin/*
 */
import { BaseService } from '../base/BaseService';
import { unwrapSalvationResponse as unwrap, salvationListOrEmpty as listOrEmpty } from './unwrapSalvationResponse';
/** Console admin (api/salvation/admin). */
export class SalvationAdminService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? SalvationAdminService.DEFAULT_SERVICE_PATH,
            basePath: SalvationAdminService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    adminUrl(endpoint, params) {
        return this.buildUrlWithParams(`admin/${endpoint}`, params);
    }
    static peopleParams(query = {}) {
        return {
            teamId: query.teamId, statusId: query.statusId, stage: query.stage, flag: query.flag, search: query.search,
            sort: query.sort, page: query.page, pageSize: query.pageSize,
        };
    }
    // ---------------------------------------------------------------- overview + people
    /** GET admin/overview: the five counters, progress, per campus and priorities. */
    async getOverview(teamId) {
        return unwrap(await this.getOverviewRaw(teamId), 'Overview not found');
    }
    getOverviewRaw(teamId) {
        return this.http.get(this.adminUrl('overview', { teamId }));
    }
    /** GET admin/people: the filtered, paged list. */
    async getPeople(query = {}) {
        return unwrap(await this.getPeopleRaw(query), 'People not found');
    }
    getPeopleRaw(query = {}) {
        return this.http.get(this.adminUrl('people', SalvationAdminService.peopleParams(query)));
    }
    /** GET admin/people/export (Salvation.Export, audited): the filtered list as raw text/csv. */
    exportPeopleCsv(query = {}) {
        return this.http.getBlob(this.adminUrl('people/export', SalvationAdminService.peopleParams(query)));
    }
    async getPerson(convertId) {
        return unwrap(await this.getPersonRaw(convertId), 'Person not found');
    }
    getPersonRaw(convertId) {
        return this.http.get(this.adminUrl(`people/${convertId}`));
    }
    /** POST admin/people (teamID required): 409 duplicate_convert ("convertId:N" in errors[1]) unless confirmNotDuplicate. */
    async addPerson(request) {
        return unwrap(await this.addPersonRaw(request), 'The person could not be added');
    }
    addPersonRaw(request) {
        return this.http.post(this.adminUrl('people'), request);
    }
    /** PUT admin/people/{id}: correct details (409 stale when expectedRowVersion no longer matches). */
    async updatePerson(convertId, request) {
        return unwrap(await this.updatePersonRaw(convertId, request), 'The person could not be saved');
    }
    updatePersonRaw(convertId, request) {
        return this.http.put(this.adminUrl(`people/${convertId}`), request);
    }
    /** POST admin/people/{id}/follow-ups: logged as the signed-in leader. */
    async logFollowUp(convertId, request) {
        return unwrap(await this.logFollowUpRaw(convertId, request), 'The follow-up could not be saved');
    }
    logFollowUpRaw(convertId, request) {
        return this.http.post(this.adminUrl(`people/${convertId}/follow-ups`), request);
    }
    /** POST admin/people/{id}/assign: the first Lead / Backup (409 assignment_permanent once both are set). */
    async assign(convertId, request) {
        return unwrap(await this.assignRaw(convertId, request), 'The assignment could not be saved');
    }
    assignRaw(convertId, request) {
        return this.http.post(this.adminUrl(`people/${convertId}/assign`), request);
    }
    /** POST admin/people/{id}/correct-assignment: change a permanent pair with a reason (Salvation.Assignments.Correct). */
    async correctAssignment(convertId, request) {
        return unwrap(await this.correctAssignmentRaw(convertId, request), 'The assignment could not be corrected');
    }
    correctAssignmentRaw(convertId, request) {
        return this.http.post(this.adminUrl(`people/${convertId}/correct-assignment`), request);
    }
    async reopen(convertId, request) {
        return unwrap(await this.reopenRaw(convertId, request), 'The person could not be reopened');
    }
    reopenRaw(convertId, request) {
        return this.http.post(this.adminUrl(`people/${convertId}/reopen`), request);
    }
    async close(convertId, request) {
        return unwrap(await this.closeRaw(convertId, request), 'The person could not be closed');
    }
    closeRaw(convertId, request) {
        return this.http.post(this.adminUrl(`people/${convertId}/close`), request);
    }
    async reviewDone(convertId, request = {}) {
        return unwrap(await this.reviewDoneRaw(convertId, request), 'The review could not be saved');
    }
    reviewDoneRaw(convertId, request = {}) {
        return this.http.post(this.adminUrl(`people/${convertId}/review-done`), request);
    }
    /** POST admin/teams/{teamId}/allocate/preview: what auto-allocation would do (nothing is saved). */
    async previewAllocation(teamId) {
        return unwrap(await this.previewAllocationRaw(teamId), 'Allocation preview not available');
    }
    previewAllocationRaw(teamId) {
        return this.http.post(this.adminUrl(`teams/${teamId}/allocate/preview`), {});
    }
    /** POST admin/teams/{teamId}/allocate: gives everyone in follow-up without a Lead or Backup the least-loaded member. */
    async allocate(teamId) {
        return unwrap(await this.allocateRaw(teamId), 'Allocation failed');
    }
    allocateRaw(teamId) {
        return this.http.post(this.adminUrl(`teams/${teamId}/allocate`), {});
    }
    // ---------------------------------------------------------------- teams
    async getTeams() {
        return listOrEmpty(await this.getTeamsRaw());
    }
    getTeamsRaw() {
        return this.http.get(this.adminUrl('teams'));
    }
    /** GET admin/campuses: active campuses (Location Management) with the team already set up for each. */
    async getCampuses() {
        return listOrEmpty(await this.getCampusesRaw());
    }
    getCampusesRaw() {
        return this.http.get(this.adminUrl('campuses'));
    }
    /** POST admin/teams (admins only; one active team per campus). */
    async createTeam(request) {
        return unwrap(await this.createTeamRaw(request), 'The team could not be created');
    }
    createTeamRaw(request) {
        return this.http.post(this.adminUrl('teams'), request);
    }
    async updateTeam(teamId, request) {
        return unwrap(await this.updateTeamRaw(teamId, request), 'The team could not be saved');
    }
    updateTeamRaw(teamId, request) {
        return this.http.put(this.adminUrl(`teams/${teamId}`), request);
    }
    async getTeamMembers(teamId) {
        return listOrEmpty(await this.getTeamMembersRaw(teamId));
    }
    getTeamMembersRaw(teamId) {
        return this.http.get(this.adminUrl(`teams/${teamId}/members`));
    }
    /** POST admin/teams/{teamId}/members (userId required): accessGranted=false = the portal role still has to be given. */
    async addTeamMember(teamId, request) {
        return unwrap(await this.addTeamMemberRaw(teamId, request), 'The team member could not be added');
    }
    addTeamMemberRaw(teamId, request) {
        return this.http.post(this.adminUrl(`teams/${teamId}/members`), request);
    }
    async updateTeamMember(teamId, teamMemberId, request) {
        return unwrap(await this.updateTeamMemberRaw(teamId, teamMemberId, request), 'The team member could not be saved');
    }
    updateTeamMemberRaw(teamId, teamMemberId, request) {
        return this.http.put(this.adminUrl(`teams/${teamId}/members/${teamMemberId}`), request);
    }
    /** PUT admin/teams/{teamId}/members/order: the whole ranking (every member, first = rank 1). */
    async reorderTeamMembers(teamId, request) {
        return listOrEmpty(await this.reorderTeamMembersRaw(teamId, request));
    }
    reorderTeamMembersRaw(teamId, request) {
        return this.http.put(this.adminUrl(`teams/${teamId}/members/order`), request);
    }
    // ---------------------------------------------------------------- settings
    async getSettings() {
        return unwrap(await this.getSettingsRaw(), 'Settings not found');
    }
    getSettingsRaw() {
        return this.http.get(this.adminUrl('settings'));
    }
    // ---------------------------------------------------------------- bringing in decisions (Salvation.Intake.Review)
    /** GET admin/intake: the review queue (statusId 1 waiting by default), paged, with waiting counts per channel. */
    async getIntake(query = {}) {
        return unwrap(await this.getIntakeRaw(query), 'The review queue could not be loaded');
    }
    getIntakeRaw(query = {}) {
        return this.http.get(this.adminUrl('intake', {
            statusId: query.statusId, channelId: query.channelId, sourceConfigId: query.sourceConfigId, teamId: query.teamId,
            search: query.search, page: query.page, pageSize: query.pageSize,
        }));
    }
    /** PUT admin/intake/{id}: correct a waiting decision (name, contact, date, campus) before accepting it. */
    async updateIntake(intakeId, request) {
        return unwrap(await this.updateIntakeRaw(intakeId, request), 'The decision could not be saved');
    }
    updateIntakeRaw(intakeId, request) {
        return this.http.put(this.adminUrl(`intake/${intakeId}`), request);
    }
    /** POST admin/intake/accept: waiting decisions become people (Lead and Backup shared out by load). */
    async acceptIntake(request) {
        return unwrap(await this.acceptIntakeRaw(request), 'The decisions could not be accepted');
    }
    acceptIntakeRaw(request) {
        return this.http.post(this.adminUrl('intake/accept'), request);
    }
    /** POST admin/intake/{id}/join: the decision belongs to someone already on the Salvation Team. */
    async joinIntake(intakeId, request) {
        return unwrap(await this.joinIntakeRaw(intakeId, request), 'The decision could not be joined');
    }
    joinIntakeRaw(intakeId, request) {
        return this.http.post(this.adminUrl(`intake/${intakeId}/join`), request);
    }
    /** POST admin/intake/reject: not decisions (a test, spam or someone else's card). */
    async rejectIntake(request) {
        return unwrap(await this.rejectIntakeRaw(request), 'The decisions could not be rejected');
    }
    rejectIntakeRaw(request) {
        return this.http.post(this.adminUrl('intake/reject'), request);
    }
    /** GET admin/intake/source-options/forms: CCMS forms (with entry statuses). */
    async getFormSourceOptions(search) {
        return listOrEmpty(await this.http.get(this.adminUrl('intake/source-options/forms', { search })));
    }
    /** GET admin/intake/source-options/pco-forms: Planning Centre People forms. */
    async getPcoFormSourceOptions(search, includeArchived = false) {
        return listOrEmpty(await this.http.get(this.adminUrl('intake/source-options/pco-forms', { search, includeArchived })));
    }
    /** GET admin/intake/source-options/pco-lists: Planning Centre People lists. */
    async getPcoListSourceOptions(search) {
        return listOrEmpty(await this.http.get(this.adminUrl('intake/source-options/pco-lists', { search })));
    }
    /** POST admin/intake/pull: pull decisions from a CCMS form or a Planning Centre form or list into the queue. */
    async pullIntake(request) {
        return unwrap(await this.pullIntakeRaw(request), 'The decisions could not be pulled');
    }
    pullIntakeRaw(request) {
        return this.http.post(this.adminUrl('intake/pull'), request);
    }
    /** POST admin/intake/rows: rows read from a spreadsheet in the browser. */
    async addIntakeRows(request) {
        return unwrap(await this.addIntakeRowsRaw(request), 'The rows could not be added');
    }
    addIntakeRowsRaw(request) {
        return this.http.post(this.adminUrl('intake/rows'), request);
    }
    /** GET admin/intake/sources: saved sources with their waiting and accepted counts. */
    async getIntakeSources() {
        return listOrEmpty(await this.http.get(this.adminUrl('intake/sources')));
    }
    async updateIntakeSource(sourceConfigId, request) {
        return unwrap(await this.http.put(this.adminUrl(`intake/sources/${sourceConfigId}`), request), 'The source could not be saved');
    }
    /** POST admin/intake/sources/{id}/check: pull what is new since the last check. */
    async checkIntakeSource(sourceConfigId) {
        return unwrap(await this.http.post(this.adminUrl(`intake/sources/${sourceConfigId}/check`), {}), 'The source could not be checked');
    }
    // ---------------------------------------------------------------- editable lookups (Salvation.Settings.Manage)
    async addDecisionSource(request) {
        return unwrap(await this.http.post(this.adminUrl('decision-sources'), request), 'The decision source could not be added');
    }
    async updateDecisionSource(decisionSourceId, request) {
        return unwrap(await this.http.put(this.adminUrl(`decision-sources/${decisionSourceId}`), request), 'The decision source could not be saved');
    }
    async updateMilestone(milestoneId, request) {
        return unwrap(await this.http.put(this.adminUrl(`milestones/${milestoneId}`), request), 'The milestone could not be saved');
    }
    /** PUT admin/settings (Salvation.Settings.Manage): partial update. */
    async saveSettings(request) {
        return unwrap(await this.saveSettingsRaw(request), 'Settings could not be saved');
    }
    saveSettingsRaw(request) {
        return this.http.put(this.adminUrl('settings'), request);
    }
}
SalvationAdminService.DEFAULT_SERVICE_PATH = 'salvation';
SalvationAdminService.BASE_PATH = 'api/salvation';
//# sourceMappingURL=SalvationAdminService.js.map